import React, { useState, useEffect } from 'react';
import { Copy, QrCode, Save } from 'lucide-react';
import Popup from '../common/Popup';

const ApiKeysPanel = ({ backendUrl }) => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copySuccess, setCopySuccess] = useState('');
  
  // Nowe stany dla konfiguracji Blissaro
  const [blissaroConfig, setBlissaroConfig] = useState({
    apiUrl: '',
    apiKey: ''
  });
  const [blissaroLoading, setBlissaroLoading] = useState(false);
  const [blissaroSaving, setBlissaroSaving] = useState(false);
  
  // Stan dla popup
  const [popupConfig, setPopupConfig] = useState(null);
  const showPopup = (config) => setPopupConfig(config);
  const hidePopup = () => setPopupConfig(null);

  const fetchApiKeys = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/admin/api-keys`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Nie udało się pobrać kluczy API');
      }
      const data = await response.json();
      setApiKeys(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Pobierz konfigurację Blissaro
  const fetchBlissaroConfig = async () => {
    setBlissaroLoading(true);
    try {
      const response = await fetch(`${backendUrl}/admin/blissaro/config`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setBlissaroConfig({
          apiUrl: data.apiUrl || 'https://blissaro.pl/api',
          apiKey: data.apiKey || ''
        });
      }
    } catch (err) {
      console.error('Błąd pobierania konfiguracji Blissaro:', err);
    } finally {
      setBlissaroLoading(false);
    }
  };

  // Zapisz konfigurację Blissaro
  const saveBlissaroConfig = async () => {
    if (!blissaroConfig.apiUrl || !blissaroConfig.apiKey) {
      alert('URL API i klucz API są wymagane');
      return;
    }

    setBlissaroSaving(true);
    try {
      const response = await fetch(`${backendUrl}/admin/blissaro/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(blissaroConfig),
      });
      
      if (!response.ok) {
        throw new Error('Nie udało się zapisać konfiguracji');
      }
      
      showPopup({
        title: 'Sukces',
        message: 'Konfiguracja Blissaro została zapisana pomyślnie!',
        type: 'success',
        size: 'compact'
      });
    } catch (err) {
      showPopup({
        title: 'Błąd',
        message: `Błąd zapisywania konfiguracji: ${err.message}`,
        type: 'error',
        size: 'compact'
      });
    } finally {
      setBlissaroSaving(false);
    };
  };

  useEffect(() => {
    fetchApiKeys();
    fetchBlissaroConfig();
  }, [backendUrl]);

  const handleGenerateKey = async (name) => {
    try {
      const response = await fetch(`${backendUrl}/admin/api-keys/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ name }),
      });
      if (!response.ok) {
        throw new Error('Nie udało się wygenerować klucza');
      }
      fetchApiKeys();
    } catch (err) {
      alert(err.message);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess('Skopiowano!');
      setTimeout(() => setCopySuccess(''), 2000);
    }, () => {
      setCopySuccess('Błąd kopiowania');
      setTimeout(() => setCopySuccess(''), 2000);
    });
  };

  if (loading) {
    return <div>Ładowanie...</div>;
  }

  if (error) {
    return <div style={{ color: "red" }}>Błąd: {error}</div>;
  }

  const templateCheckKey = apiKeys.find(key => key.name === 'template-check');
  const externalOrdersKey = apiKeys.find(key => key.name === 'external_orders_api');

  const renderKeySection = (title, description, keyData, keyName) => (
    <div className="api-key-section orders-table" style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ccc', borderRadius: '8px' }}>
      <h4 style={{ marginTop: 0 }}>{title}</h4>
      <p>{description}</p>
      {keyData ? (
        <div className="api-key-display">
          <table className="orders-table">
            <tbody>
              <tr>
                <td style={{ width: '120px' }}><strong>Nazwa:</strong></td>
                <td>{keyData.name}</td>
              </tr>
              <tr>
                <td><strong>Klucz:</strong></td>
                <td>
                  <code style={{ background: '#f0f0f0', padding: '2px 4px', borderRadius: '4px', marginRight: '10px' }}>
                    {keyData.key}
                  </code>
                  <button onClick={() => copyToClipboard(keyData.key)} className="orders-button" style={{ padding: '5px 10px' }}>
                    <Copy size={16} />
                  </button>
                </td>
              </tr>
              <tr>
                <td><strong>Utworzono:</strong></td>
                <td>{new Date(keyData.createdAt).toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <p>Brak klucza. Wygeneruj nowy.</p>
      )}
      <button onClick={() => handleGenerateKey(keyName)} className="orders-button" style={{ marginTop: '1rem' }}>
        {keyData ? 'Wygeneruj ponownie' : 'Wygeneruj Klucz'}
      </button>
    </div>
  );

  // Nowa sekcja dla konfiguracji Blissaro
  const renderBlissaroConfigSection = () => (
    <div className="api-key-section orders-table" style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #007bff', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
      <h4 style={{ marginTop: 0, display: 'flex', alignItems: 'center' }}>
        <QrCode size={20} style={{ marginRight: '10px' }} />
        Konfiguracja Integracji Blissaro
      </h4>
      <p>Skonfiguruj połączenie z API Blissaro do generowania kodów QR dla gości weselnych.</p>
      
      {blissaroLoading ? (
        <div>Ładowanie konfiguracji...</div>
      ) : (
        <div>
          <table className="orders-table">
            <tbody>
              <tr>
                <td style={{ width: '120px' }}><strong>URL API:</strong></td>
                <td>
                  <input
                    type="url"
                    value={blissaroConfig.apiUrl}
                    onChange={(e) => setBlissaroConfig({...blissaroConfig, apiUrl: e.target.value})}
                    placeholder="https://blissaro.pl/api"
                    style={{ 
                      width: '300px', 
                      padding: '8px', 
                      border: '1px solid #ddd', 
                      borderRadius: '4px',
                      marginRight: '10px'
                    }}
                  />
                </td>
              </tr>
              <tr>
                <td><strong>Klucz API:</strong></td>
                <td>
                  <input
                    type="text"
                    value={blissaroConfig.apiKey}
                    onChange={(e) => setBlissaroConfig({...blissaroConfig, apiKey: e.target.value})}
                    placeholder="bls_your_api_key_here"
                    style={{ 
                      width: '400px', 
                      padding: '8px', 
                      border: '1px solid #ddd', 
                      borderRadius: '4px',
                      marginRight: '10px'
                    }}
                  />
                  {blissaroConfig.apiKey && (
                    <button 
                      onClick={() => copyToClipboard(blissaroConfig.apiKey)} 
                      className="orders-button" 
                      style={{ padding: '5px 10px' }}
                    >
                      <Copy size={16} />
                    </button>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
          
          <div style={{ marginTop: '1rem', display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button 
              onClick={saveBlissaroConfig} 
              disabled={blissaroSaving}
              className="orders-button" 
              style={{ 
                backgroundColor: '#28a745', 
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
            >
              <Save size={16} />
              {blissaroSaving ? 'Zapisywanie...' : 'Zapisz Konfigurację'}
            </button>
            
            <div style={{ fontSize: '13px', color: '#666' }}>
              💡 <strong>Wskazówka:</strong> Klucz API możesz uzyskać z panelu administratora Blissaro
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="orders-container" style={{ marginTop: 20 }}>
        <h3>Zarządzanie Kluczami API</h3>
        {copySuccess && <div style={{ color: 'green', marginBottom: '1rem' }}>{copySuccess}</div>}
        
        {/* Nowa sekcja Blissaro na górze */}
        {renderBlissaroConfigSection()}
        
        {renderKeySection(
          'Klucz API do tworzenia zamówień',
          <>Ten klucz jest używany w publicznym endpoincie <code>/public/api/orders</code> do tworzenia nowych zamówień przez zewnętrzne aplikacje.</>,
          externalOrdersKey,
          'external_orders_api'
        )}

        {renderKeySection(
          'Klucz API do sprawdzania istnienia szablonu',
          <>Ten klucz jest używany w publicznym endpoincie <code>/public/templates/check/:templateId/:apiKey</code> do weryfikacji, czy dany szablon istnieje w systemie.</>,
          templateCheckKey,
          'template-check'
        )}
      </div>
      
      {/* Popup */}
      {popupConfig && (
        <Popup 
          config={popupConfig}
          onClose={hidePopup}
        />
      )}
    </>
  );
};

export default ApiKeysPanel;
